import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

type ThemeMode = 'system' | 'light' | 'dark' | 'base';
type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
  theme: ThemeMode;
  systemTheme: ResolvedTheme; // actual OS/browser preference
  resolvedTheme: ResolvedTheme; // resolved to 'light' or 'dark' for color-scheme and internal logic
  setTheme: (mode: ThemeMode) => void;
};

const STORAGE_KEY = 'sf-theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark'; // Default to dark if system preference can't be determined
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const loadStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'base';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system' || stored === 'base') {
    return stored;
  }
  return 'base'; // Default to base theme if stored value is invalid
};

// This function now takes the *selected mode* and the *resolved color scheme*
const applyTheme = (selectedMode: ThemeMode, resolvedColorScheme: ResolvedTheme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Clear existing classes
  root.classList.remove('light');
  root.classList.remove('dark');

  if (selectedMode === 'base') {
    // For 'base' theme, apply no explicit class, so :root styles apply
    // But set color-scheme to dark as the base theme is dark-dominant
    root.style.colorScheme = 'dark';
  } else {
    // For 'light', 'dark', or 'system' (which resolves to 'light' or 'dark')
    root.classList.add(resolvedColorScheme);
    root.style.colorScheme = resolvedColorScheme;
  }
};

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [theme, setTheme] = useState<ThemeMode>(() => loadStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  // resolvedTheme now always outputs 'light' or 'dark' for browser color-scheme
  const resolvedTheme = useMemo<ResolvedTheme>(() => {
    if (theme === 'system') return systemTheme;
    if (theme === 'base') return 'dark'; // 'base' is dark-dominant
    return theme; // 'light' or 'dark' directly
  }, [theme, systemTheme]);

  useEffect(() => {
    applyTheme(theme, resolvedTheme); // Pass both selected theme and resolved color scheme
  }, [theme, resolvedTheme]);

  useEffect(() => {
    const handler = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    const mql = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;

    if (mql) {
      mql.addEventListener('change', handler);
    }

    return () => {
      if (mql) mql.removeEventListener('change', handler);
    };
  }, []);

  const handleSetTheme = (mode: ThemeMode) => {
    setTheme(mode);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
  };

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      systemTheme,
      resolvedTheme,
      setTheme: handleSetTheme,
    }),
    [theme, systemTheme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
};
